import { useCallback } from 'react'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import { getFileMetadata, uploadToDirectUrl } from 'Pods/ThreeDStudio/Hooks/directUpload'

/**
 * Uploads an image File as a user-upload Generator::Asset (direct upload),
 * optionally tied to an existing session. Used by the AI Assistant's Create and
 * Edit tabs to turn reference images into reusable assets.
 *
 * @returns {(file: File, options?: { sessionId?: number }) => Promise<number>}
 *   Resolves with the created asset's id; rejects when the asset request or the
 *   direct upload fails.
 */
export default function useImageAssetUpload() {
  const { postData } = useApiPost()

  return useCallback(async (file, { sessionId } = {}) => {
    const blob = await getFileMetadata(file)
    const data = { asset_type: 'image', blob }
    if (sessionId) {
      data.generator_session_id = sessionId
    }

    const response = await new Promise((resolve, reject) => {
      postData({ path: '/api/internal/generator/assets', data, onSuccess: resolve, onError: reject })
    })

    const { id, direct_upload: directUpload } = response.data
    if (!directUpload?.url || !directUpload?.headers) {
      throw new Error('Invalid direct upload response')
    }
    await uploadToDirectUrl(directUpload.url, directUpload.headers, file)

    return id
  }, [postData])
}
